<template>
	<BaseLayout :footer-full="true" :include-padding="false">
		<BasePlaceHolder
			:placehold="loading"
			:section-count="3"
			:line-count="5"
			:class="[{ 'py-100': loading }, 'container']"
		>
			<div class="pt-200 pb-100 pb-200-md">
				<h1 class="heading-1 text-align-center">Offering Circulars</h1>
			</div>
			<section class="grid-md">
				<article class="col-12-md position-relative">
					<div v-if="tableOfContents" id="table-of-contents" class="module mb-300">
						<div class="module-header">
							<h2 class="heading-5">Table of Contents</h2>
						</div>
						<div class="module-section">
							<RichTextComponent :blocks="tableOfContents" />
						</div>
					</div>
					<div v-if="taxForms" id="tax-forms" class="module mb-300">
						<div class="module-header">
							<h2 class="heading-5">Tax Forms 8937</h2>
						</div>
						<div class="module-section">
							<RichTextComponent :blocks="taxForms" />
						</div>
					</div>
					<div v-if="disclaimer" id="disclaimer" class="module mb-300">
						<div class="module-header">
							<h2 class="heading-5">Disclaimer</h2>
						</div>
						<div class="module-section">
							<RichTextComponent :blocks="disclaimer" />
						</div>
					</div>
					<div v-if="registeredFunds" id="registered-funds" class="module mb-300">
						<div class="module-header">
							<h2 class="heading-5">Registered Funds</h2>
						</div>
						<div class="module-section">
							<div v-for="(item, index) in registeredFunds" :key="index">
								<div v-if="hideFrames" :id="item.anchor.current">
									<p class="mb-100">
										{{ index + 1 }}. The <span v-html="item.title" /> offering circular can be
										<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
											>downloaded here</BaseLink
										>.
									</p>
								</div>
								<div v-else :id="item.anchor.current">
									<h3 class="body-lg">
										{{ index + 1 }}. <span v-html="item.title" />
										<BaseCtaLink
											classes="float-right"
											icon="arrow"
											:link="{
												href: item.document.asset.url,
												target: '_blank',
												text: 'View file'
											}"
										/>
									</h3>
									<div class="iframe-container mb-100">
										<iframe :src="item.document.asset.url" :title="item.title" loading="lazy">
											<p>
												Your browser does not support iFrames. The offering circular can be
												<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
													>downloaded here</BaseLink
												>.
											</p>
										</iframe>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="regAOfferings" id="regulation-a-offerings" class="module mb-300">
						<div class="module-header">
							<h2 class="heading-5">Regulation A Offerings</h2>
						</div>
						<div class="module-section">
							<div v-for="(item, index) in regAOfferings" :key="index">
								<div v-if="hideFrames" :id="item.anchor.current">
									<p class="mb-100">
										{{ index + 1 }}. The <span v-html="item.title" /> offering circular can be
										<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
											>downloaded here</BaseLink
										>.
									</p>
								</div>
								<div v-else :id="item.anchor.current">
									<h3 class="body-lg">
										{{ index + 1 }}. <span v-html="item.title" />
										<BaseCtaLink
											classes="float-right"
											icon="arrow"
											:link="{
												href: item.document.asset.url,
												target: '_blank',
												text: 'View file'
											}"
										/>
									</h3>
									<div class="iframe-container mb-100">
										<iframe :src="item.document.asset.url" :title="item.title" loading="lazy">
											<p>
												Your browser does not support iFrames. The offering circular can be
												<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
													>downloaded here</BaseLink
												>.
											</p>
										</iframe>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="otherOfferings" id="other-offerings" class="module mb-300">
						<div class="module-header">
							<h2 class="heading-5">Other Offerings</h2>
						</div>
						<div class="module-section">
							<div v-for="(item, index) in otherOfferings" :key="index">
								<div v-if="hideFrames" :id="item.anchor.current">
									<p class="mb-100">
										{{ index + 1 }}. The <span v-html="item.title" /> offering circular can be
										<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
											>downloaded here</BaseLink
										>.
									</p>
								</div>
								<div v-else :id="item.anchor.current">
									<h3 class="body-lg">
										{{ index + 1 }}. <span v-html="item.title" />
										<BaseCtaLink
											classes="float-right"
											icon="arrow"
											:link="{
												href: item.document.asset.url,
												target: '_blank',
												text: 'View file'
											}"
										/>
									</h3>
									<div class="iframe-container mb-100">
										<iframe :src="item.document.asset.url" :title="item.title" loading="lazy">
											<p>
												Your browser does not support iFrames. The offering circular can be
												<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
													>downloaded here</BaseLink
												>.
											</p>
										</iframe>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="regDOfferings" id="regulation-d-offerings" class="module mb-300">
						<div class="module-header">
							<h2 class="heading-5">Regulation D Offerings</h2>
						</div>
						<div class="module-section">
							<div v-for="(item, index) in regDOfferings" :key="index">
								<div v-if="hideFrames" :id="item.anchor.current">
									<p class="mb-100">
										{{ index + 1 }}. The <span v-html="item.title" />
										can be
										<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
											>downloaded here</BaseLink
										>.
									</p>
								</div>
								<div v-else :id="item.anchor.current">
									<h3 class="body-lg">
										{{ index + 1 }}. <span v-html="item.title" />
										<BaseCtaLink
											classes="float-right"
											icon="arrow"
											:link="{
												href: item.document.asset.url,
												target: '_blank',
												text: 'View file'
											}"
										/>
									</h3>
									<div class="iframe-container mb-100">
										<iframe :src="item.document.asset.url" :title="item.title" loading="lazy">
											<p>
												Your browser does not support iFrames. The offering circular can be
												<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
													>downloaded here</BaseLink
												>.
											</p>
										</iframe>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="advisorsDisclosures" id="advisors-disclosures" class="module mb-300">
						<div class="module-header">
							<h2 class="heading-5">Fundrise Advisors Disclosure Documents</h2>
						</div>
						<div class="module-section">
							<div v-for="(item, index) in advisorsDisclosures" :key="index">
								<div v-if="hideFrames" :id="item.anchor.current">
									<p class="mb-100">
										{{ index + 1 }}. The {{ item.title }} offering circular can be
										<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
											>downloaded here</BaseLink
										>.
									</p>
								</div>
								<div v-else :id="item.anchor.current">
									<h3 class="body-lg">
										{{ index + 1 }}. {{ item.title }}
										<BaseCtaLink
											classes="float-right"
											icon="arrow"
											:link="{
												href: item.document.asset.url,
												target: '_blank',
												text: 'View file'
											}"
										/>
									</h3>
									<div class="iframe-container mb-100">
										<iframe :src="item.document.asset.url" :title="item.title" loading="lazy">
											<p>
												Your browser does not support iFrames. The offering circular can be
												<BaseLink :link="{ href: item.document.asset.url, target: '_blank' }"
													>downloaded here</BaseLink
												>.
											</p>
										</iframe>
									</div>
								</div>
							</div>
						</div>
					</div>
				</article>
			</section>
		</BasePlaceHolder>
	</BaseLayout>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { getOfferingCircularsPage } from '@api/sanity/graphql/offering-circulars-page';
import { OfferingCircularsDocument } from 'types/sanity/offering-circulars';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityRichTextBlock } from 'types/sanity';
import { scrollToAnchor } from '@utils/scroll-to';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';

const route = useRoute();

const loading = ref(true);
const hideFrames = ref(false);
const routeName = ref('offering-circulars');

const tableOfContents = ref<Array<SanityRichTextBlock> | null>([]);
const taxForms = ref<Array<SanityRichTextBlock> | null>([]);
const disclaimer = ref<Array<SanityRichTextBlock> | null>([]);
const registeredFunds = ref<Array<OfferingCircularsDocument> | null>([]);
const regAOfferings = ref<Array<OfferingCircularsDocument> | null>([]);
const regDOfferings = ref<Array<OfferingCircularsDocument> | null>([]);
const otherOfferings = ref<Array<OfferingCircularsDocument> | null>([]);
const advisorsDisclosures = ref<Array<OfferingCircularsDocument> | null>([]);

watch(loading, (value) => {
	if (!value) {
		const anchor = route.hash;
		if (anchor) {
			nextTick(() => {
				scrollToAnchor(anchor.replace('#', ''));
			});
		}
	}
});

async function setup(): Promise<void> {
	useHead({
		title: 'Offering Circulars',
		meta: [
			{ property: 'og:title', content: 'Offering Circulars' },
			{ name: 'description', content: 'Offering Circulars' },
			{ property: 'og:description', content: 'Offering Circulars' }
		]
	});
	if (route.name) {
		hideFrames.value = route.name === 'offering-circulars-links';
		routeName.value = route.name as string;
	}

	try {
		const page = await getOfferingCircularsPage();
		tableOfContents.value = page?.tableOfContents ?? null;
		taxForms.value = page?.taxForms ?? null;
		disclaimer.value = page?.disclaimer ?? null;
		registeredFunds.value = page?.registeredFunds ?? null;
		regAOfferings.value = page?.regulationAOfferings ?? null;
		regDOfferings.value = page?.regulationDOfferings ?? null;
		otherOfferings.value = page?.otherOfferings ?? null;
		advisorsDisclosures.value = page?.advisorsDisclosureDocuments ?? null;

		loading.value = false;
	} catch {}
}

setup();
</script>

<style lang="scss">
.iframe-container {
	overflow: auto;
	-webkit-overflow-scrolling: touch;
}

iframe {
	width: 100%;
	height: 500px;
}

.sanity-rich-text-container {
	a {
		text-decoration: none;
	}

	ol {
		ol {
			list-style-type: disc !important;
		}
		li {
			margin-top: unset;
		}
	}
}
</style>
